import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Video, Download, User, Briefcase, Sparkles } from "lucide-react";
import DataTable from "@/components/DataTable";
import ScoreBadge from "@/components/ScoreBadge";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import JobPreviewDialog from "@/components/JobPreviewDialog";
import DecisionMemoDialog from "@/components/DecisionMemoDialog";

/**
 * Phase 78 — Interview Decision Center
 *
 * Every interview round across the company in one sortable/filterable table:
 * who interviewed, what was rated/noted, outcome, candidate match score —
 * plus inline voting and one-click candidate/job previews. The decision log.
 */
const OUTCOMES = ["advance", "hold", "no_hire", "strong_hire", "hire", "Pass", "Fail", "Pending"];

function outcomeTint(o) {
    switch ((o || "").toLowerCase().replace(" ", "_")) {
        case "strong_hire":
        case "hire":
        case "pass":
        case "advance": return "border-emerald-300 bg-emerald-50 text-emerald-700";
        case "hold":
        case "pending": return "border-amber-300 bg-amber-50 text-amber-700";
        case "fail":
        case "no_hire": return "border-rose-300 bg-rose-50 text-rose-700";
        default: return "border-border bg-secondary/50 text-muted-foreground";
    }
}

export default function InterviewHistoryPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState({ from: "", to: "", outcome: "" });
    const [profile, setProfile] = useState(null);   // {candidateId, applicationId}
    const [jobPreview, setJobPreview] = useState(null); // jobId
    const [memoFor, setMemoFor] = useState(null);   // {candidateId, jobId, candidateName, jobTitle}

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 500 };
            if (filter.from) params.period_from = filter.from;
            if (filter.to) params.period_to = filter.to + "T23:59";
            if (filter.outcome) params.outcome = filter.outcome;
            const { data } = await api.get("/interviews/history", { params });
            setRows(data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const openProfile = (r) => {
        if (!r.candidate_id) return;
        setProfile({ candidateId: r.candidate_id, applicationId: r.application?.id || r.application_id || null });
    };

    const columns = useMemo(() => [
        {
            key: "candidate", label: "Candidate", width: 200, sortable: true,
            accessor: (r) => r.candidate?.name || "",
            render: (r) => (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openProfile(r); }}
                    className="text-left min-w-0 w-full group"
                    data-testid={`dc-candidate-${r.id}`}
                >
                    <div className="font-medium text-ikb group-hover:underline truncate">{r.candidate?.name || "—"}</div>
                    {r.candidate?.current_title && <div className="text-[10px] text-foreground/60 truncate">{r.candidate.current_title}</div>}
                </button>
            ),
        },
        {
            key: "match_score", label: "Match", width: 80, sortable: true, filterable: false, align: "right",
            accessor: (r) => r.application?.match_score ?? -1,
            render: (r) => r.application?.match_score !== undefined && r.application?.match_score !== null
                ? <ScoreBadge score={r.application.match_score} size="sm" />
                : <span className="text-foreground/30">—</span>,
        },
        {
            key: "job", label: "Job", width: 180, sortable: true,
            accessor: (r) => r.job?.title || "",
            render: (r) => r.job?.title ? (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setJobPreview(r.job_id); }}
                    className="text-left hover:underline truncate w-full text-xs"
                    data-testid={`dc-job-${r.id}`}
                >
                    {r.job.title}
                </button>
            ) : <span className="text-foreground/30">—</span>,
        },
        {
            key: "round", label: "Round", width: 130, sortable: true,
            accessor: (r) => r.round_name || r.round || "",
            render: (r) => (
                <span className="text-xs">{r.round_name || r.round || "—"}{r.round_number ? <span className="text-foreground/50"> #{r.round_number}</span> : null}</span>
            ),
        },
        {
            key: "scheduled_at", label: "Scheduled", width: 130, sortable: true, filterable: false,
            render: (r, v) => <span className="font-mono text-xs">{(v || "").slice(0, 16).replace("T", " ") || "—"}</span>,
        },
        {
            key: "interviewers", label: "Interviewers", width: 170, sortable: true,
            accessor: (r) => ((r.interviewer_names || []).join(", ") || (r.interviewers || []).map((u) => u.name || u.email || "").join(", ")),
            render: (r) => {
                const names = (r.interviewer_names || []).length ? r.interviewer_names : (r.interviewers || []).map((u) => u.name || u.email).filter(Boolean);
                return names.length ? <span className="text-xs truncate" title={names.join(", ")}>{names.join(", ")}</span> : <span className="text-foreground/30">—</span>;
            },
        },
        {
            key: "created_by_name", label: "Recruiter", width: 130, sortable: true,
            render: (r, v) => v ? <span className="text-xs truncate">{v}</span> : <span className="text-foreground/30">—</span>,
        },
        { key: "mode", label: "Mode", width: 90, sortable: true, render: (r, v) => <span className="text-xs">{v || "—"}</span> },
        {
            key: "status", label: "Status", width: 100, sortable: true,
            render: (r, v) => <span className="text-[10px] font-mono uppercase tracking-widest">{v || "Scheduled"}</span>,
        },
        {
            key: "outcome", label: "Outcome", width: 110, sortable: true,
            render: (r, v) => (
                <span className={`px-1.5 py-0.5 border font-mono uppercase tracking-widest text-[10px] ${outcomeTint(v)}`} data-testid={`dc-outcome-${r.id}`}>
                    {(v || "—").replace("_", " ")}
                </span>
            ),
        },
        {
            key: "score", label: "Rating", width: 75, sortable: true, filterable: false, align: "right",
            accessor: (r) => r.score ?? -1,
            render: (r) => r.score !== undefined && r.score !== null
                ? <span className={`font-mono text-xs font-bold ${r.score >= 7 ? "text-emerald-700" : r.score >= 4 ? "text-amber-700" : "text-rose-700"}`}>{r.score}/10</span>
                : <span className="text-foreground/30">—</span>,
        },
        {
            key: "feedback", label: "Notes", width: 220, sortable: false,
            render: (r, v) => v
                ? <span className="text-xs text-foreground/80 line-clamp-2" title={v}>{v}</span>
                : <span className="text-foreground/30">—</span>,
        },
        {
            key: "votes", label: "Team vote", width: 150, sortable: true, filterable: false,
            accessor: (r) => r.application?.vote_summary?.net ?? 0,
            render: (r) => r.application?.id ? (
                <div onClick={(e) => e.stopPropagation()} data-testid={`dc-votes-${r.id}`}>
                    <CandidateVoteBar applicationId={r.application.id} initial={r.application.vote_summary || null} size="sm" />
                </div>
            ) : <span className="text-foreground/30">—</span>,
        },
    ], []); // eslint-disable-line react-hooks/exhaustive-deps

    const renderActions = (r) => (
        <>
            <button type="button" onClick={() => openProfile(r)} className="p-1 border border-border hover:border-ikb hover:text-ikb inline-flex" title="Candidate quick view" data-testid={`dc-action-profile-${r.id}`}>
                <User className="w-3 h-3" />
            </button>
            {r.job_id && (
                <button type="button" onClick={() => setJobPreview(r.job_id)} className="p-1 border border-border hover:border-ikb hover:text-ikb inline-flex" title="Job quick view" data-testid={`dc-action-job-${r.id}`}>
                    <Briefcase className="w-3 h-3" />
                </button>
            )}
            {r.candidate_id && r.job_id && (
                <button
                    type="button"
                    onClick={() => setMemoFor({
                        candidateId: r.candidate_id, jobId: r.job_id,
                        candidateName: r.candidate?.name, jobTitle: r.job?.title,
                    })}
                    className="p-1 border border-border hover:border-ikb hover:text-ikb inline-flex"
                    title="Draft decision memo with AI"
                    data-testid={`dc-action-memo-${r.id}`}
                >
                    <Sparkles className="w-3 h-3" />
                </button>
            )}
            {(r.meet_link || r.meeting_url) && (
                <a href={r.meet_link || r.meeting_url} target="_blank" rel="noreferrer" className="p-1 border border-border hover:border-ikb hover:text-ikb inline-flex" title="Join meeting">
                    <Video className="w-3 h-3" />
                </a>
            )}
        </>
    );

    const exportCsv = () => {
        const header = ["Candidate", "Match", "Job", "Round", "Scheduled at", "Interviewers", "Recruiter", "Mode", "Status", "Outcome", "Rating", "Notes"];
        const lines = [header.join(",")].concat(rows.map((r) => [
            r.candidate?.name || "", r.application?.match_score ?? "", r.job?.title || "",
            r.round_name || r.round || "", (r.scheduled_at || "").slice(0, 16),
            ((r.interviewer_names || []).length ? r.interviewer_names : (r.interviewers || []).map((u) => u.name || u.email)).join("; "),
            r.created_by_name || "", r.mode || "", r.status || "", r.outcome || "", r.score ?? "", r.feedback || "",
        ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")));
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `interviews-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="max-w-[1600px] mx-auto p-3 sm:p-6" data-testid="interview-history-page">
            <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Decision center</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-1">Interview decision log</h1>
                    <p className="text-muted-foreground text-sm mt-2 max-w-2xl">Every interview round with outcome, interviewers, notes, ratings and match score — vote inline and open candidate or job previews without leaving the table.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="rounded-none h-9 w-38" data-testid="ih-filter-from" />
                    <span className="text-xs text-muted-foreground">→</span>
                    <Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="rounded-none h-9 w-38" data-testid="ih-filter-to" />
                    <select value={filter.outcome} onChange={(e) => setFilter({ ...filter, outcome: e.target.value })} className="rounded-none h-9 border border-border px-2 text-sm bg-white" data-testid="ih-filter-outcome">
                        <option value="">All outcomes</option>
                        {OUTCOMES.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
                    </select>
                    <Button variant="outline" onClick={load} disabled={loading} className="rounded-none h-9" data-testid="ih-refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </Button>
                    <Button variant="outline" onClick={exportCsv} className="rounded-none h-9" data-testid="ih-export"><Download className="w-3.5 h-3.5 mr-1.5" /> CSV</Button>
                </div>
            </div>

            <DataTable
                columns={columns}
                rows={rows}
                freezeFirstColumn
                rowKey={(r) => r.id}
                onRowClick={(r) => openProfile(r)}
                actions={renderActions}
                storageKey="interview-decision-center"
                testId="dc-table"
                loading={loading}
                emptyMessage="No interviews found in this window."
            />

            {profile && (
                <CandidateProfileDialog
                    open={!!profile}
                    onClose={() => setProfile(null)}
                    candidateId={profile.candidateId}
                    applicationId={profile.applicationId}
                />
            )}
            <JobPreviewDialog open={!!jobPreview} onClose={() => setJobPreview(null)} jobId={jobPreview} />
            {memoFor && (
                <DecisionMemoDialog
                    open={!!memoFor}
                    onClose={() => setMemoFor(null)}
                    candidateId={memoFor.candidateId}
                    jobId={memoFor.jobId}
                    candidateName={memoFor.candidateName}
                    jobTitle={memoFor.jobTitle}
                />
            )}
        </div>
    );
}
